/**
 * ProtectedRoute - Route protection component
 * Redirects unauthenticated users to login and provides role-based access control
 * Supports loading states and flexible redirect logic
 */

import { type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuthState } from '../../context/AuthContext';

interface ProtectedRouteProps {
  children: ReactNode;
  requiredRole?: 'user' | 'admin' | 'moderator';
  redirectTo?: string;
  fallback?: ReactNode;
}

export default function ProtectedRoute({
  children,
  requiredRole,
  redirectTo = '/login',
  fallback,
}: ProtectedRouteProps) {
  const { isAuthenticated, isLoading, user } = useAuthState();
  const location = useLocation();

  // Show loading state while checking authentication
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    const returnUrl = `${location.pathname}${location.search}${location.hash}`;
    const hasReturnUrl = redirectTo.includes('returnUrl=');
    const separator = redirectTo.includes('?') ? '&' : '?';
    const target = hasReturnUrl
      ? redirectTo
      : `${redirectTo}${separator}returnUrl=${encodeURIComponent(returnUrl)}`;

    return (
      <Navigate
        to={target}
        replace
      />
    );
  }

  // Check role-based access if required
  if (requiredRole && user) {
    const hasRequiredRole = checkUserRole(user.role, requiredRole);
    
    if (!hasRequiredRole) {
      // Show fallback component or redirect to unauthorized page
      if (fallback) {
        return <>{fallback}</>;
      }
      
      return (
        <Navigate
          to="/unauthorized"
          state={{ from: location, requiredRole }}
          replace
        />
      );
    }
  }

  // Render protected content
  return <>{children}</>;
}

/**
 * Check if user role meets the required role level
 */
function checkUserRole(userRole: string, requiredRole: string): boolean {
  const roleHierarchy = {
    user: 1,
    moderator: 2,
    admin: 3,
  };

  const userLevel = roleHierarchy[userRole as keyof typeof roleHierarchy] || 0;
  const requiredLevel = roleHierarchy[requiredRole as keyof typeof roleHierarchy] || 0;

  return userLevel >= requiredLevel;
}

/**
 * Hook to check if current user has required role
 */
export function useRequiredRole(requiredRole: 'user' | 'admin' | 'moderator'): boolean {
  const { user } = useAuthState();
  
  if (!user) return false;
  
  return checkUserRole(user.role, requiredRole);
}

/**
 * Hook to check if current user is admin
 */
export function useIsAdmin(): boolean {
  return useRequiredRole('admin');
}

/**
 * Hook to check if current user is moderator or admin
 */
export function useIsModerator(): boolean {
  return useRequiredRole('moderator');
}